import { useQuery } from '@tanstack/react-query';
import { USD_DECIMALS } from '@ui/constants/numbersDecimals';
import { BASIS_POINTS_DIVISOR } from '@ui/constants/uiConfig';
import { useDataChainId } from '@ui/hooks/datachain';
import { toReadableValue } from '@ui/lib/bignumber';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

import TradingConfigFacet from '../../abis/TradingConfigFacet';
import { getTradingConfigFacet } from '../../contract-instances/getTradingConfigFacet';
import { ExtractReturn } from '../../types/ExtractFunctionReturn';

export type ContractTradingConfig = Readonly<
  ExtractReturn<typeof TradingConfigFacet, 'getTradingConfig'>
>;

export type TradingConfig = Omit<
  ContractTradingConfig,
  'executionFeeUsd' | 'minNotionalUsd'
> & {
  executionFeeUsd: BigNumber;
  minNotionalUsd: BigNumber;
};

export const useGetTradingConfig = () => {
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });
  const { address } = useAccount();

  const TradingConfigFacet = useMemo(() => {
    if (!client) return;

    return getTradingConfigFacet({
      chainId,
      publicClient: client,
    });
  }, [chainId, client?.uid]);

  return useQuery({
    queryKey: ['TradingConfigFacet.getTradingConfig', chainId, client?.uid],
    queryFn: async (): Promise<TradingConfig | undefined> => {
      const re = await TradingConfigFacet?.read.getTradingConfig();

      if (isNil(re)) return undefined;

      return {
        ...re,
        executionFeeUsd: toReadableValue(re.executionFeeUsd, USD_DECIMALS),
        minNotionalUsd: toReadableValue(re.minNotionalUsd, USD_DECIMALS),
        maxTakeProfitP: re.maxTakeProfitP / BASIS_POINTS_DIVISOR,
      };
    },
    enabled: !!TradingConfigFacet,
  });
};
